import {DateTime} from 'luxon';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {BillingUpgradeDiamondIcon} from '../../../../../../libs/shared/billing/web/src/components/billingUpgradeDiamondIcon';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  DateRangePeriod,
  DateRangePeriodsEnum,
  StartAndEndDates,
} from '../../../../../../libs/shared/core/src/helpers/models/dateIntervalModels';
import {getUserTimeZone} from '../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {DateRangeComboBoxDropdown} from '../../../../../../libs/shared/ui/src/components/datetime/dateRangeComboBoxDropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getCalendarWeekStartDay} from '../../../../../store/data/preferences/preferencesSelectors';
import {trackSearchHintDropdownOpen} from '../../searchTrackingHelpers';
import {SearchHintItemPill} from '../searchHintItemPill';
import {parsePeriod} from './dateUtils';

const DropdownOpener = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledIcon = styled(Icon)`
  transform: rotate(-90deg);
  color: ${(p) => p.theme.greys.shade70};
`;

interface SearchHintDateProps {
  query: string | undefined;
  isDisabled: boolean;
  trackDateHintChange: (trimmedUpdatedQuery: string, modifier: ModifiersEnum | 'before_after') => void;
  queryHash: string | undefined;
  onQueryChange: (arg: string) => void;
}

export const SearchHintDate: React.FC<SearchHintDateProps> = (props) => {
  const {query, isDisabled, trackDateHintChange, queryHash, onQueryChange} = props;

  const calendarWeekStartDay = useAppSelector(getCalendarWeekStartDay);
  const timezone = useAppSelector(getUserTimeZone);
  const selectedStartDate = useMemo(() => extractDate(query, 'after', timezone), [query, timezone]);
  const selectedEndDate = useMemo(() => extractDate(query, 'before', timezone), [query, timezone]);
  const selectedPeriod = useMemo(
    () => getSelectedDateRangePeriod({startDate: selectedStartDate, endDate: selectedEndDate}),
    [selectedStartDate, selectedEndDate],
  );

  const isActive = selectedStartDate !== undefined || selectedEndDate !== undefined;

  const onDateSelect = (dates: StartAndEndDates, done: () => void) => {
    const afterDateString = `${dates.startDate?.toFormat('yyyy-MM-dd')}`;
    const beforeDateString = `${dates.endDate?.toFormat('yyyy-MM-dd')}`;
    const after = `after:${afterDateString}`;
    const before = `before:${beforeDateString}`;
    const during = `during:${afterDateString}`;
    const splitQuery = query ? query.split(' ') : [];

    if (splitQuery.includes(after) && splitQuery.includes(before)) {
      return;
    }
    const hasDuring = splitQuery.some((e) => e.startsWith('during:'));

    const elementsToKeep = splitQuery?.filter(
      (e) =>
        // If we have during, we don't want to keep after and before.
        // if we don't have during, we want to keep them in the same order already present in the query.
        (!hasDuring && (e === after || e === before)) ||
        // this excludes the before and after from the query unless the dates haven't changed.
        (!e.startsWith('after:') && !e.startsWith('before:') && !e.startsWith('during:')),
    );

    // if before and after are already in the query, and they haven't changed, we don't need to update the query.
    if (elementsToKeep.includes(after) && elementsToKeep.includes(before)) {
      return;
    }

    // if the before and after are the same, we can replace them with during.
    if (afterDateString === beforeDateString && afterDateString !== 'undefined') {
      elementsToKeep.push(during);
    } else {
      // else we need to add the before and/or after to the query.
      if (after !== 'after:undefined' && !elementsToKeep.includes(after)) {
        elementsToKeep.push(after);
      }

      if (before !== 'before:undefined' && !elementsToKeep.includes(before)) {
        elementsToKeep.push(before);
      }
    }
    const trimmedUpdatedQuery = elementsToKeep.join(' ').trim();

    onQueryChange(trimmedUpdatedQuery);
    if (dates.startDate && dates.endDate) {
      trackDateHintChange(trimmedUpdatedQuery, 'before_after');
    } else if (dates.startDate) {
      trackDateHintChange(trimmedUpdatedQuery, ModifiersEnum.AFTER);
    } else if (dates.endDate) {
      trackDateHintChange(trimmedUpdatedQuery, ModifiersEnum.BEFORE);
    }

    // This is a little hack to call onRequestClose after the search is done.
    // Calling onRequestClose toggles the popover.
    // When selecting a period, the popover should close after the search is done.
    // When selecting a range through custom range, the popover doesn't automatically close.
    if (!getSelectedDateRangePeriod(dates)) {
      done();
    }
  };

  const interactionId = `search-hint-date`;

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          isActive={isActive}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        >
          <DateTitle
            selectedPeriod={selectedPeriod}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
          />
        </SearchHintItemPill>
      )}
      renderModal={(popoverTransferProps) => (
        <DateRangeComboBoxDropdown
          popoverTransferProps={popoverTransferProps}
          onDateSelect={(date) => {
            onDateSelect(date, popoverTransferProps.onRequestClose);
          }}
          selectedPeriod={selectedPeriod?.period.period}
          calendarWeekStartDay={calendarWeekStartDay}
          onCancel={popoverTransferProps.onRequestClose}
          startDate={selectedStartDate}
          endDate={selectedEndDate}
          shouldHideLabel
          hasCheckbox
          hasSlidingWindow
          customPeriods={customSearchHintPeriods}
          onClearButtonClick={() => {
            onDateSelect({}, popoverTransferProps.onRequestClose);
          }}
          confirmButtonLabel={<Trans id="apply">Apply</Trans>}
          renderTimePeriodUnavailableIndicator={() => <BillingUpgradeDiamondIcon />}
        />
      )}
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          trackSearchHintDropdownOpen({
            queryHash,
            modifier: 'date',
          });
        }
      }}
    />
  );
};

export const customSearchHintPeriods: Array<DateRangePeriod> = [
  {
    key: DateRangePeriodsEnum.OLDER_THAN_A_WEEK,
    label: <Trans id="older-than-a-week">Older than a week</Trans>,
    period: {
      period: DateRangePeriodsEnum.OLDER_THAN_A_WEEK,
      endDate: DateTime.local().minus({week: 1}),
    },
  },

  {
    key: DateRangePeriodsEnum.OLDER_THAN_A_MONTH,
    label: <Trans id="older-than-a-month">Older than a month</Trans>,
    period: {
      period: DateRangePeriodsEnum.OLDER_THAN_A_MONTH,
      endDate: DateTime.local().minus({month: 1}),
    },
  },

  {
    key: DateRangePeriodsEnum.OLDER_THAN_6_MONTHS,
    label: <Trans id="older-than-6-months"> Older than 6 months </Trans>,
    period: {
      period: DateRangePeriodsEnum.OLDER_THAN_6_MONTHS,
      endDate: DateTime.local().minus({months: 6}),
    },
  },

  {
    key: DateRangePeriodsEnum.OLDER_THAN_A_YEAR,
    label: <Trans id="older-than-a-year"> Older than a year</Trans>,
    period: {
      period: DateRangePeriodsEnum.OLDER_THAN_A_YEAR,
      endDate: DateTime.local().minus({year: 1}),
    },
  },
];

type DateTitleProps = {
  selectedPeriod?: DateRangePeriod;
  startDate?: DateTime;
  endDate?: DateTime;
};

export const DateTitle: FC<DateTitleProps> = ({selectedPeriod, startDate, endDate}) => {
  const startDateString = startDate?.toFormat('MMM d, yyyy');
  const endDateString = endDate?.toFormat('MMM d, yyyy');
  if (selectedPeriod) {
    return selectedPeriod.label;
  }
  if (startDate && endDate) {
    if (startDateString === endDateString) {
      return (
        <>
          <Trans id="during">During</Trans> {startDateString}
        </>
      );
    }

    return (
      <DropdownOpener>
        <>{startDateString}</>
        <StyledIcon name="arrowSmall" />
        <>{endDateString}</>
      </DropdownOpener>
    );
  }
  if (startDate && !endDate) {
    return (
      <>
        <Trans id="after">After</Trans> {startDateString}
      </>
    );
  }
  if (!startDate && endDate) {
    return (
      <>
        <Trans id="before">Before</Trans> {endDateString}
      </>
    );
  }

  return <Trans id="date">Date</Trans>;
};

// TODO(ldemarne): Move to query helpers.
export const parseDateModifierDate = (dateString: string, key: 'before' | 'after', timezone?: string) => {
  const period = parsePeriod(dateString, timezone);
  if (!period) {
    return undefined;
  }

  return DateTime.fromMillis(key === 'after' ? period.start : period.end);
};

const extractDate = (query: string | undefined, key: 'before' | 'after', timezone?: string) => {
  const splitQuery = query ? query.split(' ') : [];
  if (splitQuery.length === 0) {
    return undefined;
  }
  // If during is written multiple times, it won't result in anything.
  if (splitQuery.filter((e) => e.startsWith(`during:`)).length > 1) {
    return undefined;
  }

  // If there's one during, we use it since it takes precedence over before and after.
  const during = splitQuery.find((e) => e.startsWith(`during:`));
  if (during) {
    return parseDateModifierDate(during.split(':')[1], key, timezone);
  }
  const foundAfters = splitQuery.filter((e) => e.startsWith(`${key}:`));
  if (foundAfters.length === 0) {
    return undefined;
  }

  const dates = foundAfters
    .map((e) => e.split(':')[1])
    .map((dateString) => parseDateModifierDate(dateString, key, timezone))
    .filter((e): e is DateTime => !!e)
    .sort((a, b) => (a > b ? 1 : -1));

  return key === 'after' ? dates[dates.length - 1] : dates[0];
};

export function getSelectedDateRangePeriod(
  dateRange: StartAndEndDates | undefined,
): DateRangePeriod | undefined {
  if (!dateRange) {
    return undefined;
  }

  const {startDate, endDate} = dateRange;

  return customSearchHintPeriods.find((e) => {
    const startPeriodDateTimeString = e.period.startDate?.toFormat('yyyy-MM-dd');
    const endPeriodDateTimeString = e.period.endDate?.toFormat('yyyy-MM-dd');
    if (
      startPeriodDateTimeString === startDate?.toFormat('yyyy-MM-dd') &&
      endPeriodDateTimeString === endDate?.toFormat('yyyy-MM-dd')
    ) {
      return true;
    }
    return false;
  });
}
